import store from 'state/store.js';
import { getTotalChargeFromBaseCurrencyAmount } from 'utils/helper-functions/amount-conversion';
import { round } from 'utils/round-functions/round';
import { AvailableCurrency } from '@pano-project/utils/getLimitsForCurrency';
import { getWalletCurrencyLimits } from 'utils/hooks/digital-wallets/helpers';

const getConvertedBaseCurrencyLimits = (chargeCurrency: string, gateway: any) => {
  const {
    profileInfo: { base_currency = '', available_currencies: { currencies = [] } = {} } = {},
  } = store.getState();

  const currencyInfo =
    getWalletCurrencyLimits(chargeCurrency) ||
    gateway?.payment_information?.currency_list?.[chargeCurrency] ||
    currencies?.find((currency: AvailableCurrency) => currency.code === chargeCurrency);

  if (!currencyInfo) return null;
  const { min_amount, max_amount, rate } = currencyInfo;
  return {
    min_amount: round(min_amount / rate, base_currency),
    max_amount: round(max_amount / rate, base_currency),
  };
};

export const getGlobalBaseCurrencyInfo = (chargeCurrency: string) => {
  const {
    profileInfo: { base_currency = '', available_currencies: { currencies = [] } = {} } = {},
  } = store.getState();

  const currencyInfo = currencies?.find(
    (currency: AvailableCurrency) => currency.code === chargeCurrency,
  );

  if (!currencyInfo) return null;
  const { min_amount, max_amount, rate, fees } = currencyInfo;

  return {
    processingFees: fees,
    processingRate: rate,
    baseMinLimit: round(min_amount / rate, base_currency),
    baseMaxLimit: round(max_amount / rate, base_currency),
  };
};

export const calculateMaxPossibleBaseAmount = (chargeCurrency: string, gateway: any) => {
  const { max_amount: maxLimit } = getConvertedBaseCurrencyLimits(chargeCurrency, gateway) || {};
  return maxLimit ?? null;
};

export const calculateMinPossibleBaseAmount = (chargeCurrency: string, gateway: any) => {
  const { min_amount: minLimit } = getConvertedBaseCurrencyLimits(chargeCurrency, gateway) || {};
  return minLimit ?? null;
};

export const calculateMaxPossibleProcessingAmount = (
  chargeCurrency: string,
  gateway: Record<string, any>,
) => {
  const { max_amount: maxLimit } = getConvertedBaseCurrencyLimits(chargeCurrency, gateway) || {};
  return getTotalChargeFromBaseCurrencyAmount({ amount: maxLimit, chargeCurrency, gateway });
};

export const calculateMinPossibleProcessingAmount = (
  chargeCurrency: string,
  gateway: Record<string, any>,
) => {
  const { min_amount: minLimit } = getConvertedBaseCurrencyLimits(chargeCurrency, gateway) || {};
  return getTotalChargeFromBaseCurrencyAmount({ amount: minLimit, chargeCurrency, gateway });
};

export const getProcessingFeeFromAvailableCurrencies = (chargeCurrency: string) => {
  const { processingFees } = getGlobalBaseCurrencyInfo(chargeCurrency) || {};
  const feeObjectsArray =
    processingFees?.map((fees: Record<string, any>) => ({ [chargeCurrency]: [fees] })) || undefined;

  if (!feeObjectsArray?.length) return null;
  return feeObjectsArray;
};

export const calculateGlobalProcessingLimits = (chargeCurrency: string) => {
  const { baseMaxLimit, baseMinLimit, processingRate } =
    getGlobalBaseCurrencyInfo(chargeCurrency) || {};
  const feeObjectsArray = getProcessingFeeFromAvailableCurrencies(chargeCurrency);

  // calculate processing amount without fees
  if (!feeObjectsArray?.length) {
    return {
      minLimit: getTotalChargeFromBaseCurrencyAmount({
        chargeCurrency,
        amount: baseMinLimit,
        predefinedCurrencyInfo: { rate: processingRate },
        gateway: undefined,
      }),
      maxLimit: getTotalChargeFromBaseCurrencyAmount({
        chargeCurrency,
        amount: baseMaxLimit,
        predefinedCurrencyInfo: { rate: processingRate },
        gateway: undefined,
      }),
    };
  }

  // get arrays of all possible min/max limits affected by fee
  const minLimitsArray = feeObjectsArray.map((feeObject: Record<string, any>) =>
    getTotalChargeFromBaseCurrencyAmount({
      chargeCurrency,
      amount: baseMinLimit,
      predefinedFees: feeObject,
      predefinedCurrencyInfo: { rate: processingRate },
      gateway: undefined,
    }),
  );
  const maxLimitsArray = feeObjectsArray.map((feeObject: Record<string, any>) =>
    getTotalChargeFromBaseCurrencyAmount({
      chargeCurrency,
      amount: baseMaxLimit,
      predefinedFees: feeObject,
      predefinedCurrencyInfo: { rate: processingRate },
      gateway: undefined,
    }),
  );

  // return min/max possible limit
  return {
    minLimit: Math.min(...minLimitsArray),
    maxLimit: Math.max(...maxLimitsArray),
  };
};
